import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { createPaymentIntent } from '../../features/payments/api';
import { useStripePublishableKey } from '../../hooks/usePartnerBranding';
import { resolveBaselineBalance } from '../../hooks/usePollCreditRefreshAfterPurchase';
import {
  writePurchaseMarker,
  clearPurchaseMarker,
  type CreditPurchaseInfo,
} from '../../features/payments/purchaseMarker';
import styles from './PurchasePointsModal.module.css';
import { brandConfig } from '../../config/brands';
import { absoluteAppUrl } from '../../utils/navigation';
import '../../styles/buttons.css';
import '../../styles/form-controls.css';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * リダイレクトなしのインライン成功 (経路 A) 時に、ライブ反映に必要な情報を渡す。
   * 親はこれで直接ポーリングを起動できる (sessionStorage マーカーに依存しない)。
   */
  onSuccess?: (info: CreditPurchaseInfo) => void;
};

// ポイント数のオプション
type PointOption = { value: number; label: string };

const DEFAULT_POINT_OPTIONS: PointOption[] = [
  { value: 1000, label: '1,000ポイント' },
  { value: 3000, label: '3,000ポイント' },
  { value: 5000, label: '5,000ポイント' },
  { value: 10000, label: '10,000ポイント' },
  { value: 50000, label: '50,000ポイント' },
];

// usodeki は少額中心のプリセット (10,000 / 50,000 を出さず、50 / 100 / 500 を追加)
const USODEKI_POINT_OPTIONS: PointOption[] = [
  { value: 50, label: '50ポイント' },
  { value: 100, label: '100ポイント' },
  { value: 500, label: '500ポイント' },
  { value: 1000, label: '1,000ポイント' },
  { value: 3000, label: '3,000ポイント' },
  { value: 5000, label: '5,000ポイント' },
];

function getPointOptions(partnerCode: string | null | undefined): PointOption[] {
  return partnerCode === 'usodeki' ? USODEKI_POINT_OPTIONS : DEFAULT_POINT_OPTIONS;
}

const CUSTOM_VALUE = 'custom';

export function PurchasePointsModal({ isOpen, onClose, onSuccess }: Props) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>('1000');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  // 購入ポイント数 / PaymentIntent ID は決済確定直後の残高ライブ反映に使う。
  const [purchasedPoints, setPurchasedPoints] = useState<number | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

  // テナント別 Stripe publishable key を runtime 解決 (fail-closed)。
  // env key へのフォールバックは partner_code==='default' のときだけ許可し、
  // 他パートナーで publishable key 未配信なら pk='' → 決済をブロックする
  // (default の LightMedia key を誤って使わせない)。
  const { stripePublishableKey, partnerCode, brandingSettled } = useStripePublishableKey();
  const pointOptions = useMemo(() => getPointOptions(partnerCode), [partnerCode]);
  const defaultOption = useMemo(() => String(pointOptions[0].value), [pointOptions]);
  const pk = brandingSettled
    ? (stripePublishableKey
        || (partnerCode === 'default'
              ? (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '')
              : ''))
    : '';
  const stripePromise = useMemo(() => (pk ? loadStripe(pk) : null), [pk]);
  const stripeUnavailable = brandingSettled && !stripePromise;

  // モーダルが開いたときにリセット
  useEffect(() => {
    if (isOpen) {
      setSelectedOption(defaultOption);
      setCustomAmount('');
      setError(null);
      setClientSecret(null);
      setPurchasedPoints(null);
      setPaymentIntentId(null);
    }
  }, [isOpen, defaultOption]);

  // branding が開いた後に確定してプリセットが切り替わった場合、
  // 選択中の値が新しい候補に無ければ既定値へ戻す (無効オプションの残留を防ぐ)。
  useEffect(() => {
    if (selectedOption === CUSTOM_VALUE) return;
    if (!pointOptions.some((opt) => String(opt.value) === selectedOption)) {
      setSelectedOption(defaultOption);
    }
  }, [pointOptions, defaultOption, selectedOption]);

  // 表示中は背景 (body) のスクロールを無効化し、モーダル内だけスクロール可能にする。
  // Stripe 決済フォーム (PaymentElement) が縦に伸びる Step2 で背景が動くのを防ぐ。
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // 外側クリック (overlay クリック) で閉じる。決済フロー中は閉じない。
  const handleOverlayClick = () => {
    if (!clientSecret) {
      onClose();
    }
  };

  const handleProceedToPayment = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Stripe 初期化が確定するまで / 利用不可なら決済を作らない (fail-closed)
    if (!brandingSettled) {
      setError('決済の準備中です。しばらくしてから再度お試しください。');
      return;
    }
    if (stripeUnavailable) {
      setError('このアカウントでは現在オンライン決済をご利用いただけません。');
      return;
    }

    // ポイント数を取得
    let amount_points: number;
    if (selectedOption === CUSTOM_VALUE) {
      const parsed = parseInt(customAmount, 10);
      if (isNaN(parsed) || parsed < 50 || parsed > 100000) {
        setError('50〜100,000ポイントの範囲で入力してください');
        return;
      }
      amount_points = parsed;
    } else {
      amount_points = parseInt(selectedOption, 10);
      // 有効なオプションかどうかを検証（サイレントなフォールバックを防ぐ）
      const validOptions = pointOptions.map(opt => opt.value);
      if (!validOptions.includes(amount_points)) {
        console.error(`Invalid point option: ${selectedOption}, valid options: ${validOptions.join(', ')}`);
        setError('無効なポイント数が選択されています。ページを再読み込みしてください。');
        return;
      }
    }

    setIsCreatingIntent(true);

    try {
      const response = await createPaymentIntent({
        amount_points,
      });
      setClientSecret(response.client_secret);
      setPurchasedPoints(response.amount_points);
      setPaymentIntentId(response.payment_intent_id);
    } catch (err: any) {
      console.error('Failed to create payment intent:', err);
      const errorMessage = err?.response?.data?.error || err?.message || '決済の準備に失敗しました';
      setError(errorMessage);
    } finally {
      setIsCreatingIntent(false);
    }
  };

  const handleCancel = () => {
    if (!clientSecret) {
      onClose();
    } else {
      // 決済フロー中の場合は確認
      if (window.confirm('決済を中止してもよろしいですか？')) {
        // 決済確定前に中止したのでマーカーが残っていれば消す (誤起動防止)。
        clearPurchaseMarker();
        setClientSecret(null);
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  // ライトテーマ (usodeki 等) では Stripe PaymentElement も明色に合わせる
  const isLight = brandConfig.colorScheme === 'light';
  const appearance = {
    theme: (isLight ? 'stripe' : 'night') as 'stripe' | 'night',
  };

  return createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        className={`modal-card modal-card--md purchase-points-modal ${styles.modal}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <span className="modal-title">ポイント購入</span>
          <button
            type="button"
            onClick={handleCancel}
            className="modal-close-button"
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          {!clientSecret ? (
            // Step 1: ポイント数選択
            <form onSubmit={handleProceedToPayment} className={styles.form}>
              <div className="form-label-common">
                <span>購入ポイント数</span>
                <select
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  className={styles.select}
                >
                  {pointOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} (¥{option.value.toLocaleString()})
                    </option>
                  ))}
                  <option value={CUSTOM_VALUE}>カスタム...</option>
                </select>
              </div>

              {selectedOption === CUSTOM_VALUE && (
                <div className="form-label-common">
                  <span>カスタム金額</span>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="50〜100000"
                    min="50"
                    max="100000"
                    step="10"
                    className={styles.input}
                    autoFocus
                  />
                  <span className={styles.hint}>
                    1ポイント = 1円 (最小: 50 / 最大: 100,000)
                  </span>
                </div>
              )}

              {error && <div className={styles.error}>{error}</div>}

              <div className="modal-actions">
                <button
                  type="submit"
                  disabled={isCreatingIntent}
                  className="btn-modal btn-modal-accent"
                >
                  {isCreatingIntent ? '処理中...' : '支払いへ進む'}
                </button>
              </div>
            </form>
          ) : !brandingSettled ? (
            // Stripe 設定取得待ち (env key へフォールバックしない fail-closed)
            <div className={styles.error}>決済の準備中です...</div>
          ) : !stripePromise ? (
            // publishable key 未配信のパートナー (default 以外で未設定)
            <div className={styles.error}>
              このアカウントでは現在オンライン決済をご利用いただけません。
            </div>
          ) : (
            // Step 2: Stripe決済フォーム
            <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
              <PaymentForm
                expectedPoints={purchasedPoints ?? 0}
                paymentIntentId={paymentIntentId ?? undefined}
                onSuccess={(info) => {
                  onSuccess?.(info);
                  onClose();
                }}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Stripe決済フォームコンポーネント
function PaymentForm({ expectedPoints, paymentIntentId, onSuccess }: {
  expectedPoints: number;
  paymentIntentId?: string;
  onSuccess: (info: CreditPurchaseInfo) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // confirmPayment の「直前」に baseline を最新化し、購入マーカーを書く。
      // expectedPoints / baselineBalance は Stripe の戻りクエリでは復元できないため、
      // 3DS リダイレクト等でページを離れても拾えるようここで保存しておく (経路 B)。
      const baselineBalance = await resolveBaselineBalance(queryClient);
      const purchaseInfo: CreditPurchaseInfo = { expectedPoints, baselineBalance, paymentIntentId };
      writePurchaseMarker({ ...purchaseInfo, ts: Date.now() });

      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // SAITE のようにサブパス配信 (VITE_APP_BASENAME=/app) されるパートナーでは
          // origin 直付けだと basename が落ちて 404 になるため absoluteAppUrl を経由する。
          return_url: absoluteAppUrl('/payment-complete'),
        },
      });

      if (submitError) {
        // 決済確定に失敗。マーカーが残ると次回マウントで誤起動するので消す。
        clearPurchaseMarker();
        setError(submitError.message || '決済に失敗しました');
      } else {
        // リダイレクトなしのインライン成功 (経路 A)。
        // sessionStorage に依存せず、ここで得た情報を親に直接渡してポーリングを起動させる。
        // (マーカーが書けない環境でもインライン反映が動くようにする)
        onSuccess(purchaseInfo);
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      clearPurchaseMarker();
      setError('決済処理中にエラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.paymentForm}>
      <PaymentElement />

      {error && <div className={styles.error}>{error}</div>}

      <div className="modal-actions">
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="btn-modal btn-modal-accent"
        >
          {isProcessing ? '処理中...' : '支払いを確定'}
        </button>
      </div>
    </form>
  );
}
